import { useQuery } from "@tanstack/react-query";
import React, { memo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  FacilityDetailsAnalysisChart,
  IFacilityDetailsAnalysisChartProps,
} from "./Chart";
import { FacilityDetailsAnalysisChartSelect } from "./ChartSelect";
import { IFacilityDetailsAnalysisChartOptions } from "./types";
import { ChartCollectionData } from "../../../models/ChartCollectionData";
import DataSvc from "../../../services/dataSvc";
import { ShowLastYearSelect } from "../../AnalysisCollectionsComponents/CollectionChartArea/LineChart/ShowLastYearSelect";
import { TimeUnitSelect } from "../../AnalysisCollectionsComponents/CollectionChartArea/LineChart/TimeUnitSelect";
import { TimespanPicker } from "../../DataDownloadComponents/TimespanPicker";
import { useTimespanPicker } from "../../DataDownloadComponents/TimespanPicker/hooks";

import "./styles.scss";

export interface IFacilityDetailsAnalysisProps {
  locationId: string;
}

export const FacilityDetailsAnalysis = ({
  locationId,
}: IFacilityDetailsAnalysisProps) => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`facilityDetailsPage.analysis.${key}`);

  const { data: chartCollections } = useQuery<ChartCollectionData[]>(
    ["chartCollectionsTemplate"],
    () => {
      return DataSvc.getChartCollections({
        sort: "name",
        limit: 50,
        is_template: true,
      });
    }
  );

  const [selectedChartIndex, setSelectedChartIndex] = useState<number>(0);

  // Safely get the selected chart ID from the chart collections.
  const selectedChartId =
    !!chartCollections && chartCollections?.length > selectedChartIndex
      ? chartCollections[selectedChartIndex].id
      : undefined;

  const timespan = useTimespanPicker();

  const [options, setOptions] = useState<IFacilityDetailsAnalysisChartOptions>({
    fidelity: "day",
    compareToPrev: false,
  });

  return (
    <div className="detail-group">
      <div className="group-title">
        {t("title")}
        <div className="line"></div>
      </div>
      <div className="white-border-panel facility-details-analysis padding32">
        <div className="facility-details-analysis-top-row">
          {chartCollections && (
            <FacilityDetailsAnalysisChartSelect
              chartCollections={chartCollections}
              selectedChartIndex={selectedChartIndex}
              onSelectChart={setSelectedChartIndex}
              showDescription={false}
            />
          )}
        </div>
        <div className="facility-details-analysis-second-row">
          {chartCollections && (
            <FacilityDetailsAnalysisChartSelect
              chartCollections={chartCollections}
              selectedChartIndex={selectedChartIndex}
              onSelectChart={setSelectedChartIndex}
              showDropdown={false}
            />
          )}
          <div className="facility-details-analysis-right-section">
            <TimespanPicker timespan={timespan} />
            <div className="facility-details-analysis-options">
              <div className="chart-controls-row">
                <TimeUnitSelect
                  timeUnit={options.fidelity}
                  setTimeUnit={(unit) =>
                    setOptions((o) => ({ ...o, fidelity: unit }))
                  }
                />
                <ShowLastYearSelect
                  usePrevYear={options.compareToPrev}
                  setUsePrevYear={(shouldUse) =>
                    setOptions((o) => ({ ...o, compareToPrev: shouldUse }))
                  }
                />
              </div>
            </div>
            <div id="csv-download" className="csv-download" />
          </div>
        </div>
        {selectedChartId && (
          <FacilityDetailsAnalysisChartMemo
            locationId={locationId}
            timespan={timespan.timespan}
            selectedChartId={selectedChartId}
            options={options}
            setOptions={setOptions}
          ></FacilityDetailsAnalysisChartMemo>
        )}
      </div>
    </div>
  );
};

// This component needs is memoized to prevent re-rendering of the chart
// when the TimespanPicker component is re-rendered.
const FacilityDetailsAnalysisChartMemo = memo(
  function FacilityDetailsAnalysisChartMemo(
    props: IFacilityDetailsAnalysisChartProps
  ) {
    return <FacilityDetailsAnalysisChart {...props} />;
  }
);
